import React from 'react'
import { formatIsoDateLikeForDisplay, isIsoDateLikeString } from '../utils/reportCellDisplay'
import { formatNumber, trimEmptyPivotForDisplay } from '../utils/panelWidgetRendering'
import { formatPivotValue, type PivotAggregation, type PivotResult } from '../utils/pivot'

// Shared by NewDashboard.tsx (Panel, full editing) and Dashboard.tsx
// (/home's pinned-widget preview, read-only) — one rendering of a pivot
// table instead of a second duplicate implementation.
export const PivotTableView = (props: {
  pivot: PivotResult
  rowField: string
  aggregation: PivotAggregation
  totalLabel: string
  grandTotalLabel: string
}): React.ReactNode => {
  const { rowField, aggregation, totalLabel, grandTotalLabel } = props
  // findings.md #59: hide rows/columns with no value at all (display only).
  const pivot = trimEmptyPivotForDisplay(props.pivot, aggregation)

  const displayedColumnKeys = pivot.columnKeys.length > 0 ? pivot.columnKeys : [totalLabel]
  const formatKeyForDisplay = (key: string): string => (
    isIsoDateLikeString(key) ? formatIsoDateLikeForDisplay(key) : key
  )

  const bodyRows: React.ReactNode[] = []
  for (let rowIndex = 0; rowIndex < pivot.rowKeys.length; rowIndex += 1) {
    const rowKey = pivot.rowKeys[rowIndex]
    const row = pivot.cells[rowIndex]
    const total = pivot.rowTotals[rowIndex]

    const cells: React.ReactNode[] = []
    for (let colIndex = 0; colIndex < displayedColumnKeys.length; colIndex += 1) {
      const columnKey = displayedColumnKeys[colIndex]
      const cell = row[colIndex]
      cells.push(
        <td key={columnKey} style={{ padding: 10, border: '1px solid #e5e7eb', textAlign: 'right' }}>
          {formatNumber(formatPivotValue(cell, aggregation))}
        </td>
      )
    }

    bodyRows.push(
      <tr key={rowKey}>
        <td style={{ padding: 10, border: '1px solid #e5e7eb', fontWeight: 700 }}>{formatKeyForDisplay(rowKey)}</td>
        {cells}
        {pivot.columnKeys.length > 0 && (
          <td style={{ padding: 10, border: '1px solid #e5e7eb', fontWeight: 700, textAlign: 'right' }}>
            {formatNumber(formatPivotValue(total, aggregation))}
          </td>
        )}
      </tr>
    )
  }

  const footerCells: React.ReactNode[] = []
  if (pivot.columnKeys.length > 0) {
    for (let index = 0; index < pivot.columnKeys.length; index += 1) {
      const columnKey = pivot.columnKeys[index]
      const cell = pivot.columnTotals[index]
      footerCells.push(
        <td key={columnKey} style={{ padding: 10, border: '1px solid #e5e7eb', fontWeight: 800, textAlign: 'right' }}>
          {formatNumber(formatPivotValue(cell, aggregation))}
        </td>
      )
    }
  } else {
    footerCells.push(
      <td key="total" style={{ padding: 10, border: '1px solid #e5e7eb', fontWeight: 800, textAlign: 'right' }}>
        {formatNumber(formatPivotValue(pivot.grandTotal, aggregation))}
      </td>
    )
  }

  return (
    // flex: 1 (inside the widget card's flex column) makes this the one
    // region that actually scrolls — bounded to whatever vertical space is
    // left after the header/chart above it, so its horizontal scrollbar
    // sits at a fixed, always-reachable edge instead of at the bottom of
    // however many rows the pivot has.
    <div style={{ overflow: 'auto', marginTop: 12, flex: '1 1 auto', minHeight: 0 }}>
      <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f3f4f6', textAlign: 'left' }}>
            <th style={{ padding: 10, border: '1px solid #e5e7eb' }}>{rowField}</th>
            {displayedColumnKeys.map((columnKey) => (
              <th key={columnKey} style={{ padding: 10, border: '1px solid #e5e7eb' }}>{formatKeyForDisplay(columnKey)}</th>
            ))}
            {pivot.columnKeys.length > 0 && (
              <th style={{ padding: 10, border: '1px solid #e5e7eb' }}>{totalLabel}</th>
            )}
          </tr>
        </thead>
        <tbody>{bodyRows}</tbody>
        <tfoot>
          <tr style={{ backgroundColor: '#f9fafb' }}>
            <td style={{ padding: 10, border: '1px solid #e5e7eb', fontWeight: 800 }}>{grandTotalLabel}</td>
            {footerCells}
            {pivot.columnKeys.length > 0 && (
              <td style={{ padding: 10, border: '1px solid #e5e7eb', fontWeight: 800, textAlign: 'right' }}>
                {formatNumber(formatPivotValue(pivot.grandTotal, aggregation))}
              </td>
            )}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
